// T-095 (v1.8.55, Session 43): Regeln für Änderungen an Gerichten und Zusatzleistungen im Kundenkonto, gleiche Regeln wie das
// Backend (BookingSystem.Business.Buchung.Gerichteverteilung, InvoiceService.CalculateInclusiveQuantities). Wahlgerichte sind frei
// umverteilbar (Summe höchstens leistungsberechtigte Teilnehmer mal Multiplikator), kostenpflichtige Zusatzleistungen bis zur
// Schlussrechnung reduzierbar, Inklusivleistungen ohne Wahl und Unterkünfte nur erhöhen. Die Vorschau des Servers bleibt maßgeblich.

export type AddOnArt = 'Wahlgericht' | 'Inklusiv' | 'Zusatz' | 'Unterkunft';

export interface AddOnArtQuelle {
  art?: string | null;
  isAccommodation?: boolean;
  isOptionalChoice?: boolean;
  isIncludedInTourPrice?: boolean;
  quantityFree?: number;
}

const ARTEN: AddOnArt[] = ['Wahlgericht', 'Inklusiv', 'Zusatz', 'Unterkunft'];

/** Art vom Server, sonst (älterer Server) aus den Merkmalen in der Reihenfolge der Rechnung. */
export function artVon(a: AddOnArtQuelle): AddOnArt {
  if (a.art && (ARTEN as string[]).includes(a.art)) return a.art as AddOnArt;
  if (a.isAccommodation) return 'Unterkunft';
  if (a.isOptionalChoice) return 'Wahlgericht';
  if (a.isIncludedInTourPrice || (a.quantityFree ?? 0) > 0) return 'Inklusiv';
  return 'Zusatz';
}

/** Darf die Menge unter die gebuchte Menge sinken? */
export function darfReduzieren(art: AddOnArt, zusatzReduzierbar: boolean): boolean {
  if (art === 'Wahlgericht') return true;
  if (art === 'Zusatz') return zusatzReduzierbar;
  return false;
}

/** Höchstzahl der Wahlgerichte: leistungsberechtigte Teilnehmer mal Multiplikator. */
export function maxWahlgerichte(berechtigte: number, multiplikator?: number): number {
  return Math.max(0, berechtigte) * Math.max(1, multiplikator || 1);
}

/**
 * Bezahlte Menge wie die Rechnung: Wahlgericht und Inklusiv bei Preisart "PerPerson" höchstens die Berechtigten frei, sonst alles
 * frei; Zusatzleistung und Unterkunft mit der gespeicherten freien Menge.
 */
export function bezahlteMenge(art: AddOnArt, pricingType: string | undefined, menge: number, berechtigte: number, quantityFree: number): number {
  if (menge <= 0) return 0;
  if (art === 'Wahlgericht' || art === 'Inklusiv') {
    const proPerson = !pricingType || pricingType === 'PerPerson';
    const frei = proPerson ? Math.min(menge, Math.max(0, berechtigte)) : menge;
    return menge - frei;
  }
  return Math.max(0, menge - Math.max(0, quantityFree));
}

/** Hinweiszeile zur Summe der Wahlgerichte (wie Gerichteverteilung.SummenText im Backend). */
export function summenText(summe: number, max: number): string {
  if (summe > max) return `${summe} von ${max} Gerichten gewählt: ${summe - max} zu viel, bitte reduzieren.`;
  if (summe === max) return `${summe} von ${max} Gerichten gewählt (1 Gericht je Teilnehmer, Kinder bis 2 Jahre ausgenommen).`;
  return `${summe} von ${max} Gerichten gewählt, noch ${max - summe} offen.`;
}

/**
 * Plus erlaubt? Wahlgerichte nur, solange die Summe unter dem Höchstwert liegt; andere Arten immer.
 */
export function darfErhoehen(art: AddOnArt, summeWahl: number, maxWahl: number): boolean {
  return art !== 'Wahlgericht' || summeWahl < maxWahl;
}

export interface MengenGrenzen {
  /** kleinste erlaubte Menge (0, wenn reduzierbar, sonst bisherige Menge bzw. Mitbuchung) */
  min: number;
  /** Mindestmenge je Buchung (Zusatzleistung: 0 oder mindestens diese Menge) */
  mindestMenge?: number;
  /** Höchstmenge je Buchung (leer oder 0 = ohne Grenze) */
  hoechstMenge?: number | null;
}

/**
 * Nächste Menge für Plus (richtung 1) und Minus (richtung -1), Review v1.8.55: unter der Mindestmenge springt Minus auf 0 und
 * Plus auf die Mindestmenge; über die Höchstmenge geht Plus nicht hinaus.
 */
export function naechsteMenge(aktuell: number, richtung: 1 | -1, g: MengenGrenzen): number {
  const mindest = Math.max(0, g.mindestMenge ?? 0);
  if (richtung < 0) {
    let ziel = aktuell - 1;
    if (mindest > 1 && ziel > 0 && ziel < mindest) ziel = 0;
    return Math.max(Math.max(0, g.min), ziel);
  }
  let ziel = aktuell + 1;
  if (mindest > 1 && ziel < mindest) ziel = mindest;
  if (g.hoechstMenge && g.hoechstMenge > 0 && ziel > g.hoechstMenge) return aktuell;
  return ziel;
}
